import { useState, useEffect } from 'react';
import { supabase } from '../shared/supabase';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [userForPassword, setUserForPassword] = useState(null);
  const [resetting, setResetting] = useState(false);
  const [togglingAdmin, setTogglingAdmin] = useState(null);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data: profilesData, error: profilesError } = await supabase
        .from('profiles')
        .select('*')
        .order('created_at', { ascending: false });

      if (profilesError) {
        console.error('Error fetching profiles:', profilesError);
        throw profilesError;
      }

      console.log('Profiles data:', profilesData);
      setUsers(profilesData || []);

    } catch (error) {
      console.error('Error fetching users:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async (userId, userEmail, userName) => {
    setUserForPassword({ id: userId, email: userEmail, name: userName });
    setShowPasswordModal(true);
  };

  const confirmPasswordReset = async () => {
    if (!userForPassword) {
      alert('Please select a user');
      return;
    }

    try {
      setResetting(true);

      const { error: resetError } = await supabase.auth.resetPasswordForEmail(
        userForPassword.email,
        {
          redirectTo: `${window.location.origin}/reset-password`,
        }
      );

      if (resetError) throw resetError;

      alert(`✅ Password reset email sent to ${userForPassword.email}`);
      
      setShowPasswordModal(false);
      setUserForPassword(null);

    } catch (error) {
      console.error('Error resetting password:', error);
      alert('❌ Error: ' + error.message);
    } finally {
      setResetting(false);
    }
  };

  const cancelPasswordReset = () => {
    setShowPasswordModal(false);
    setUserForPassword(null);
  };

  const handleDeleteUser = async (userId, userEmail, userName) => {
    const { data: { user: currentUser } } = await supabase.auth.getUser();
    if (currentUser?.id === userId) {
      alert('You cannot delete your own account!');
      return;
    }

    setUserToDelete({ id: userId, email: userEmail, name: userName });
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!userToDelete) return;

    try {
      setLoading(true);

      const { error: profileError } = await supabase
        .from('profiles')
        .delete()
        .eq('id', userToDelete.id);

      if (profileError) {
        console.error('Profile delete error:', profileError);
        throw profileError;
      }

      console.log('User deleted successfully');

      setShowDeleteModal(false);
      setUserToDelete(null);
      
      await fetchUsers();

      const displayName = userToDelete.name || userToDelete.email;
      alert(`✅ User "${displayName}" has been deleted successfully!`);

    } catch (error) {
      console.error('Error deleting user:', error);
      alert('❌ Error: ' + error.message + '\n\nThe user might be protected by database policies.');
      setShowDeleteModal(false);
      setUserToDelete(null);
    } finally {
      setLoading(false);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setUserToDelete(null);
  };

  const toggleAdminStatus = async (userId, userName, currentAdminStatus) => {
    console.log('toggleAdminStatus called:', { userId, userName, currentAdminStatus });
    
    // Prevent toggling your own admin status
    const { data: { user: currentUser } } = await supabase.auth.getUser();
    console.log('Current user:', currentUser?.id);
    
    if (currentUser?.id === userId) {
      alert('❌ You cannot modify your own admin status!');
      return;
    }

    const newStatus = !currentAdminStatus;
    const action = newStatus ? 'grant' : 'revoke';
    const displayName = userName || 'this user';

    console.log('About to show confirm dialog');
    const confirmed = window.confirm(`Are you sure you want to ${action} admin privileges ${newStatus ? 'to' : 'from'} ${displayName}?`);
    console.log('User confirmed:', confirmed);
    
    if (!confirmed) {
      return;
    }

    try {
      setTogglingAdmin(userId);
      console.log('Starting update - User ID:', userId, 'New admin status:', newStatus);

      const { data, error: updateError } = await supabase
        .from('profiles')
        .update({ is_admin: newStatus })
        .eq('id', userId)
        .select();

      console.log('Update completed - Data:', data, 'Error:', updateError);

      if (updateError) {
        console.error('Error toggling admin status:', updateError);
        throw updateError;
      }

      // Check if update was successful
      if (data && data.length > 0) {
        console.log('Update successful, new data:', data[0]);
        alert(`✅ Admin privileges ${newStatus ? 'granted to' : 'revoked from'} ${displayName}`);
      } else {
        console.error('No rows affected by update');
        throw new Error('Update failed - no rows affected. Check database policies.');
      }
      
      // Refresh the user list
      console.log('Refreshing user list...');
      await fetchUsers();

    } catch (error) {
      console.error('Error in toggleAdminStatus:', error);
      alert('❌ Error: ' + error.message + '\n\nPlease check:\n1. Database RLS policies allow updates\n2. You have permission to modify user roles');
    } finally {
      setTogglingAdmin(null);
      console.log('toggleAdminStatus completed');
    }
  };

  if (loading) {
    return (
      <div className="admin-content">
        <div className="content-header">
          <h1>User Management</h1>
        </div>
        <div className="loading">Loading users...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-content">
        <div className="content-header">
          <h1>User Management</h1>
        </div>
        <div className="empty-state">
          <p style={{ color: 'red' }}>Error loading users: {error}</p>
          <button onClick={fetchUsers} className="btn-primary" style={{ marginTop: '20px' }}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-content">
      {/* Password Reset Modal */}
      {showPasswordModal && userForPassword && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: '500px' }}>
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <div style={{ 
                width: '64px', 
                height: '64px', 
                background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                borderRadius: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px',
                fontSize: '32px'
              }}>
                🔑
              </div>
              <h2 style={{ margin: '0 0 8px 0', color: '#1e293b', fontSize: '24px', fontWeight: 700 }}>Reset Password</h2>
              <p style={{ margin: 0, color: '#64748b', fontSize: '15px' }}>
                {userForPassword.name || userForPassword.email}
              </p>
            </div>

            <div style={{
              background: '#f0f9ff',
              border: '2px solid #bae6fd',
              borderRadius: '12px',
              padding: '20px',
              marginBottom: '24px'
            }}>
              <p style={{ margin: '0 0 12px 0', fontWeight: '600', color: '#0c4a6e', fontSize: '15px' }}>
                📧 Send Password Reset Email
              </p>
              <p style={{ margin: '0 0 16px 0', color: '#0369a1', fontSize: '14px', lineHeight: '1.6' }}>
                The user will receive an email with a secure link to reset their password. This is the recommended and most secure method.
              </p>
              <button
                onClick={confirmPasswordReset}
                disabled={resetting}
                className="btn-primary"
                style={{ width: '100%' }}
              >
                {resetting ? 'Sending Email...' : 'Send Reset Email'}
              </button>
            </div>

            <button
              onClick={cancelPasswordReset}
              className="btn-secondary"
              style={{ width: '100%' }}
              disabled={resetting}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && userToDelete && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: '500px' }}>
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <div style={{ 
                width: '64px', 
                height: '64px', 
                background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                borderRadius: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px',
                fontSize: '32px'
              }}>
                ⚠️
              </div>
              <h2 style={{ margin: '0 0 8px 0', color: '#1e293b', fontSize: '24px', fontWeight: 700 }}>Delete User</h2>
              <p style={{ margin: 0, color: '#64748b', fontSize: '15px' }}>
                {userToDelete.name || userToDelete.email}
              </p>
            </div>

            <div style={{ 
              background: '#fef2f2', 
              border: '2px solid #fecaca',
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '24px'
            }}>
              <p style={{ margin: '0 0 12px 0', fontWeight: '600', color: '#991b1b' }}>
                This will permanently delete:
              </p>
              <ul style={{ margin: 0, paddingLeft: '20px', color: '#991b1b' }}>
                <li>User profile</li>
                <li>All session data</li>
                <li>All journal entries</li>
              </ul>
              <p style={{ margin: '12px 0 0 0', fontWeight: '600', color: '#991b1b' }}>
                ⚠️ This action cannot be undone!
              </p>
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={cancelDelete}
                className="btn-secondary"
                style={{ flex: 1 }}
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                style={{
                  flex: 1,
                  background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '10px',
                  fontSize: '15px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                Delete User
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="content-header">
        <div>
          <h1>User Management</h1>
          <div className="stats">
            <div className="stat-card">
              <h3>{users.length}</h3>
              <p>Total Users</p>
            </div>
            <div className="stat-card">
              <h3>{users.filter(u => u.is_premium).length}</h3>
              <p>Premium Users</p>
            </div>
            <div className="stat-card" style={{ background: 'linear-gradient(135deg, #818cf8 0%, #6366f1 100%)' }}>
              <h3>{users.filter(u => u.is_admin).length}</h3>
              <p>Admin Users</p>
            </div>
          </div>
        </div>
        <button onClick={fetchUsers} className="btn-primary">
          🔄 Refresh
        </button>
      </div>

      <div className="content-body">
        {users.length === 0 ? (
          <div className="empty-state">
            <p>No users found</p>
          </div>
        ) : (
          <div className="users-table-container">
            <table className="users-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Status</th>
                  <th>Sessions</th>
                  <th>Streak</th>
                  <th>Category</th>
                  <th>Joined</th>
                  <th style={{ width: '160px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id}>
                    <td>
                      <div className="user-name">
                        <strong>{user.full_name || 'No name'}</strong>
                      </div>
                    </td>
                    <td>{user.email || 'No email'}</td>
                    <td>
                      <span className={`badge ${user.is_premium ? 'premium' : 'free'}`}>
                        {user.is_premium ? 'Premium' : 'Free'}
                      </span>
                      {user.is_admin && (
                        <span className="badge" style={{ marginLeft: '8px', background: '#818cf8', color: 'white' }}>
                          ADMIN
                        </span>
                      )}
                    </td>
                    <td>{user.total_sessions || 0}</td>
                    <td>
                      <span className="streak">🔥 {user.current_streak || 0}</span>
                    </td>
                    <td>{user.favorite_category || '-'}</td>
                    <td>{user.created_at ? new Date(user.created_at).toLocaleDateString() : '-'}</td>
                    <td>
                      <div style={{ display: 'flex', gap: '6px', justifyContent: 'center', flexWrap: 'wrap' }}>
                        <button 
                          onClick={() => toggleAdminStatus(user.id, user.full_name, user.is_admin)}
                          className="btn-icon"
                          title={user.is_admin ? 'Revoke Admin' : 'Make Admin'}
                          disabled={togglingAdmin === user.id}
                          style={{ 
                            background: user.is_admin ? '#fef3c7' : '#e0e7ff', 
                            color: user.is_admin ? '#92400e' : '#4338ca',
                            fontSize: '14px',
                            width: '32px',
                            height: '32px',
                            padding: '6px',
                            opacity: togglingAdmin === user.id ? 0.5 : 1,
                            cursor: togglingAdmin === user.id ? 'not-allowed' : 'pointer'
                          }}
                        >
                          {user.is_admin ? '👑' : '⭐'}
                        </button>
                        <button 
                          onClick={() => handlePasswordReset(user.id, user.email, user.full_name)}
                          className="btn-icon"
                          title="Reset Password"
                          style={{ 
                            background: '#ddd6fe', 
                            color: '#6d28d9',
                            fontSize: '14px',
                            width: '32px',
                            height: '32px',
                            padding: '6px'
                          }}
                        >
                          🔑
                        </button>
                        <button 
                          onClick={() => handleDeleteUser(user.id, user.email, user.full_name)}
                          className="btn-icon"
                          title="Delete User"
                          style={{ 
                            background: '#fee2e2', 
                            color: '#991b1b',
                            fontSize: '14px',
                            width: '32px',
                            height: '32px',
                            padding: '6px'
                          }}
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserManagement;